import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  Image,
  ModalCloseButton,
  ModalBody,
  Heading,
  Box,
} from "@chakra-ui/react";
import diamond from "assets/diamond.png";
import { EVENTS, Mixpanel } from "lib/mixpanel";
import { useNavigate } from "react-router-dom";
import { ENV } from "utils/constants";
import { formatCurrency } from "utils/helpers";
import useGlobalStore from "zStore";
import { useTranslation } from "react-i18next";
import {
  DeviceType,
  DurationValue,
  getSubscriptionDetails,
  PlanType,
} from "./SubCard";
import { resolvePlanLogoSrc } from "./resolvePlanLogo";
import { useTenant } from "context/TenantContext";

type GoldModalProps = {
  isOpen: boolean;
  onClose: () => void;
  allSubs: Array<PlanType>;
  durationType: DurationValue;
  device: DeviceType;
  businessId: string | null;
};

const GoldModal = (props: GoldModalProps) => {
  const { t, i18n } = useTranslation("subscriptions");
  const { isOpen, onClose, allSubs, durationType, device, businessId } = props;
  const [setSub] = useGlobalStore((state) => [state.setSubscription]);
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const goldSub = allSubs.find((value: any) => value.header.name === "Gold");
  const silverSub = allSubs.find(
    (value: any) => value.header.name === "Silver"
  );
  const isIos = device === "ios";

  const goldDuration = goldSub?.price.duration[durationType];
  const silverDuration = silverSub?.price.duration[durationType];

  const goldPrice = goldDuration
    ? isIos
      ? goldDuration.ios
      : goldDuration.cost
    : 0;

  const silverPrice = silverDuration
    ? isIos
      ? silverDuration.ios
      : silverDuration.cost
    : 0;

  const priceDiff = silverDuration ? +goldPrice - +silverPrice : 0;

  const currency = silverSub ? silverSub.price.currency : "USD";

  const price = `${formatCurrency(+priceDiff, currency, i18n.language)}`;

  const handleSelection = (selectedPlan: string) => {
    if (selectedPlan === "Gold") {
      Mixpanel.track(EVENTS.GOLD_CLICKED);
    } else {
      Mixpanel.track(EVENTS.SILVER_CLICKED);
    }
    const plan = selectedPlan === "Gold" ? goldSub : silverSub;
    if (!plan) return;

    const selectedDuration = plan.price.duration[durationType];
    if (!selectedDuration) return;

    let iosExternalPlanId = selectedDuration.iosExternalPlanId || "";
    const iosPlanId = selectedDuration.iosPlanId;
    const planId = selectedDuration.id;

    if (device === "ios") {
      if (process.env.REACT_APP_CLIENT_ENV_QA === ENV.DEV) {
        if (iosExternalPlanId.includes(".OZE.")) {
          iosExternalPlanId = iosExternalPlanId.replace(
            ".OZE.",
            ".testflightqa."
          );
        }
      }
      const url = `/ios?planId=${iosPlanId}&externalPlanId=${iosExternalPlanId}`;
      const iosPage = url;
      return (window.location.href = iosPage);
    }
    const { duration, durationWord } = getSubscriptionDetails(durationType);
    const currentSub = {
      planName: plan.header.name,
      image: resolvePlanLogoSrc(tenant.favicon, plan.header.logo),
      duration,
      durationWord,
      durationType,
      date: new Date(),
      currency: plan.price.currency,
      amount: selectedPlan === "Gold" ? goldPrice : silverPrice,
      id: planId,
      businessId,
    };
    setSub({
      ...currentSub,
    });
    const location = window.location.href;
    localStorage.setItem("current-location", location);
    navigate("/order");
  };
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent p="40px 27px">
          <ModalCloseButton />
          <Image
            w="70px"
            h="78px"
            mt="4"
            mx="auto"
            objectFit="cover"
            src={diamond}
            alt={t("planPreviewAlt")}
          />
          <Heading
            mt="32px"
            color="primary"
            fontWeight={500}
            fontSize="20px"
            textAlign="center"
            lineHeight="155%"
            letterSpacing="-0.048px"
          >
            {t("goldModal.title", { price })}
          </Heading>
          <ModalBody fontSize="14px">
            <ul className="centered-list">
              <li>{t("goldModal.feature1")}</li>
              <li>{t("goldModal.feature2")}</li>
              <li>{t("goldModal.feature3")}</li>
              <li>{t("goldModal.feature4")}</li>
            </ul>
          </ModalBody>
          <Box>
            <Button
              w="100%"
              mt="32px"
              bg="primary"
              h="48px"
              onClick={() => handleSelection("Gold")}
            >
              {t("goldModal.chooseGold")}
            </Button>
            <Button
              w="100%"
              mt="17px"
              color="primary"
              h="48px"
              textDecor="underline"
              variant="link"
              onClick={() => handleSelection("Silver")}
            >
              {t("goldModal.staySilver")}
            </Button>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GoldModal;
